import { deviceApi } from "#/api/deviceApi";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { Device } from "#/types/device";
import { logger } from "#/utils/logger";
import { Alert, Button, List, Loader, Modal } from "@mantine/core";
import { IconAlertTriangle, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface DeleteDeviceModalProps {
  opened: boolean;
  onClose: () => void;
  device: Device | undefined;
  onSuccess: () => void;
}

const DeleteDeviceModal = ({
  opened,
  onClose,
  device,
  onSuccess,
}: DeleteDeviceModalProps) => {
  const { t } = useTranslation();
  const { canDelete } = usePermission();

  const { data: preview, isPending: previewPending } = useQuery({
    queryKey: ["device-delete-preview", device?.id],
    queryFn: async () => {
      if (!device) return null;
      const res = await deviceApi.getDeletePreview(device.id);
      return res.data;
    },
    enabled: opened && Boolean(device),
    staleTime: 0,
    gcTime: 0,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return await deviceApi.delete(id);
    },
    onSuccess: (res) => {
      const wasArchived = res.data?.archived === true;
      showSuccessNotification({
        message: wasArchived
          ? t("devices.delete.archiveSuccess", "Device archived successfully")
          : t("devices.delete.deleteSuccess", "Device deleted successfully"),
      });
      onClose();
      onSuccess();
    },
    onError: (error) => {
      logger.error("DeleteDeviceModal delete error:", error);
      showErrorNotification({
        message: t("devices.delete.error", "Failed to delete device"),
      });
    },
  });

  const handleConfirm = () => {
    if (device) deleteMutation.mutate(device.id);
  };

  const willArchive = preview?.will_archive === true;
  const counts = preview?.counts;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={
        willArchive
          ? t("devices.delete.archiveTitle", "Archive device")
          : t("devices.delete.deleteTitle", "Delete device")
      }
      closeOnClickOutside={false}
    >
      {previewPending || !preview ? (
        <div className="flex items-center justify-center py-8">
          <Loader size="sm" />
        </div>
      ) : willArchive ? (
        <>
          <Alert
            icon={<IconAlertTriangle size={18} />}
            color="yellow"
            className="mb-3"
          >
            {t(
              "devices.delete.archiveExplanation",
              "This device has historical data and will be archived (soft-deleted). It will no longer be able to connect and will be hidden from lists. Historical records are preserved. Archived devices cannot be restored."
            )}
          </Alert>
          <p className="mb-2">
            <strong>{device?.name ?? device?.serial_number}</strong>{" "}
            {t("devices.delete.linkedTo", "is linked to:")}
          </p>
          <List size="sm" className="mb-3">
            {counts && counts.scan_history > 0 && (
              <List.Item>
                {counts.scan_history}{" "}
                {t("devices.delete.scanHistory", "scan history entries")}
              </List.Item>
            )}
            {counts && counts.refill_history > 0 && (
              <List.Item>
                {counts.refill_history}{" "}
                {t("devices.delete.refillHistory", "refill history entries")}
              </List.Item>
            )}
            {counts && counts.rfid_tag_last_read > 0 && (
              <List.Item>
                {counts.rfid_tag_last_read}{" "}
                {t(
                  "devices.delete.rfidTagLastRead",
                  "RFID tag(s) last read by this device"
                )}
              </List.Item>
            )}
            {counts && counts.shipments > 0 && (
              <List.Item>
                {counts.shipments}{" "}
                {t(
                  "devices.delete.shipments",
                  "shipment(s) scanned by this device"
                )}
              </List.Item>
            )}
          </List>
        </>
      ) : (
        <p>
          {t(
            "devices.delete.hardDeleteConfirmation",
            "Are you sure you want to permanently delete"
          )}{" "}
          <strong>{device?.name ?? device?.serial_number}</strong>?
        </p>
      )}

      <div className="flex items-center justify-end gap-2 mt-4">
        <Button
          variant="outline"
          color="black"
          onClick={onClose}
          disabled={deleteMutation.isPending}
        >
          {t("common.cancel")}
        </Button>
        <PermissionTooltip disabled={!canDelete()}>
          <Button
            color="red"
            leftSection={<IconTrash size={18} />}
            onClick={handleConfirm}
            loading={deleteMutation.isPending}
            disabled={!canDelete() || previewPending || !preview}
          >
            {willArchive
              ? t("devices.delete.archiveButton", "Archive")
              : t("common.delete")}
          </Button>
        </PermissionTooltip>
      </div>
    </Modal>
  );
};

export default DeleteDeviceModal;
